import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ApiError } from '../../abstract/api-error';
import { AuthApiService } from '../../core/api-services/auth-api.service';
import { Profile } from '../../core/data-models/profile.model';
import { AuthService } from '../../core/services/auth.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
  profile: Profile | null = null;
  loading = true;
  actionLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private router: Router,
    private authApi: AuthApiService,
    private authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadProfile();
  }

  async loadProfile(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.profile = await firstValueFrom(this.authApi.getMe());
    } catch (error: any) {
      console.error('Load profile error:', error);
      this.errorMessage = 'Failed to load profile settings';
    } finally {
      this.loading = false;
    }
  }

  navigateToSetup2FA(): void {
    this.router.navigate(['/setup-2fa']);
  }

  async disable2FA(): Promise<void> {
    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    // Prompt for verification code
    const code = prompt('Enter your current 2FA verification code to confirm:');
    if (!code) {
      return;
    }

    this.actionLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.authApi.disableTwoFactor(code));
      this.successMessage = 'Two-factor authentication has been disabled';
      await this.loadProfile(); // Reload profile to update 2FA status
    } catch (error: unknown) {
      console.error('Disable 2FA error:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to disable 2FA. Please check your verification code.';
    } finally {
      this.actionLoading = false;
    }
  }

  async regenerateBackupCodes(): Promise<void> {
    if (!confirm('Are you sure you want to regenerate backup codes? Your existing backup codes will no longer work.')) {
      return;
    }

    this.actionLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const backupCodes = await firstValueFrom(this.authApi.regenerateBackupCodes());
      if (backupCodes.length > 0) {
        // Download backup codes automatically
        this.downloadBackupCodes(backupCodes);
        this.successMessage = 'New backup codes have been generated and downloaded';
      }
    } catch (error: unknown) {
      console.error('Regenerate backup codes error:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to regenerate backup codes';
    } finally {
      this.actionLoading = false;
    }
  }

  private downloadBackupCodes(codes: string[]): void {
    const codesText = codes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'adn-backup-codes.txt';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /** `/auth/me` does not say which 2FA method is set up, only whether 2FA is on. */
  get2FAMethodLabel(): string {
    return this.profile?.is2FAEnabled ? 'Enabled' : 'Not enabled';
  }
}
